import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { AudioShareLinkDialog } from 'app/components/Song/Audio/AudioShareLinkDialog';
import { AudioEditDialog } from 'app/components/Song/Audio/AudioEditDialog';
import { DeleteSongAudioDialog } from 'app/components/Song/Audio/DeleteSongAudioDialog';
import { MarkPrivateAudioDialog } from 'app/components/Song/Audio/MarkPrivateAudioDialog';
import {
  Audio,
  AudioFormValidationProperties,
  AudioTypesDDL,
} from 'types/audio';
import { AppDispatch } from 'app/store';
import {
  deleteSongAudioEffect,
  setSongLatestAudioVersionEffect,
  updateSongAudioEffect,
} from 'app/store/SongList/effects';
import { AudioNoteDialog } from 'app/components/Song/Audio/AudioNoteDialog';
import { AudioDialogVariant } from 'app/components/Song/Audio/AudioTableList/types';
import { MarkPublicAudioDialog } from 'app/components/Song/Audio/MarkPublicAudioDialog';
import { CreatePlaylistDialog } from 'app/components/Song/Audio/CreatePlaylistDialog';
import { Song } from 'types/song';
import { AudioSelectionItem } from 'app/components/Audio';

interface AudioDialogsProps {
  dialog?: AudioDialogVariant;
  onClose: () => void;
  audioFormValidation?: AudioFormValidationProperties;
  dispatch: AppDispatch;
  audioTypes: AudioTypesDDL;
  song?: Song;
}

export const AudioDialogs: React.FC<AudioDialogsProps> = ({
  dialog,
  onClose,
  audioFormValidation,
  dispatch,
  audioTypes,
  song,
}) => {
  if (!dialog) {
    return null;
  }
  const { type, audio } = dialog;

  const handleDelete = async () => {
    if (audio) {
      await dispatch(deleteSongAudioEffect({ audio })).unwrap();
    }
  };

  const handleSetLatestAudioVersion = (audio: Audio) =>
    dispatch(
      setSongLatestAudioVersionEffect({ audioGuid: audio.guid })
    ).unwrap();

  const setToPrivate = (audio: Audio) =>
    dispatch(
      updateSongAudioEffect({
        audio,
        formData: {
          isPrivate: true,
        },
        togglingPrivate: true,
      })
    ).unwrap();

  return (
    <>
      {type === 'share' && (
        <AudioShareLinkDialog
          audioTitle={audio.title}
          onClose={onClose}
          audioGuid={audio.guid}
          songGuid={audio.parent}
        />
      )}
      {type === 'playlist' && (
        <CreatePlaylistDialog
          open
          audio={{
            ...audio,
            songDetails: {
              title: song?.title,
              status: song?.status,
            } as AudioSelectionItem['songDetails'],
          }}
          onClose={onClose}
        />
      )}
      {audioFormValidation && type === 'edit' && (
        <AudioEditDialog
          open
          handleClose={onClose}
          onDelete={handleDelete}
          audioFormValidation={audioFormValidation}
          audioTypes={audioTypes}
          audio={audio}
          dispatch={dispatch}
        />
      )}
      {type === 'delete' && (
        <DeleteSongAudioDialog
          onClose={onClose}
          onConfirm={handleDelete}
          audio={audio}
        />
      )}
      {type === 'private' && (
        <MarkPrivateAudioDialog
          audio={audio}
          onClose={onClose}
          onConfirm={handleSetLatestAudioVersion}
        />
      )}
      {type === 'privateLatestVersion' && (
        <MarkPublicAudioDialog
          audio={audio}
          onClose={onClose}
          onConfirm={setToPrivate}
        />
      )}
      {audioFormValidation && type === 'note' && (
        <AudioNoteDialog
          open
          handleClose={onClose}
          audioFormValidation={audioFormValidation}
          audio={audio}
          dispatch={dispatch}
        />
      )}
    </>
  );
};

export type AudioDialogsContainerElement = {
  setDialogOpen: (props?: AudioDialogVariant) => void;
};

export const AudioDialogsContainer = forwardRef<
  AudioDialogsContainerElement,
  AudioDialogsProps
>(({ dispatch, audioFormValidation, audioTypes, song }, ref) => {
  const [dialogOpen, setDialogOpen] = useState<AudioDialogVariant>();

  useImperativeHandle(ref, () => ({
    setDialogOpen,
  }));

  return (
    <AudioDialogs
      dialog={dialogOpen}
      onClose={() => setDialogOpen(undefined)}
      dispatch={dispatch}
      audioFormValidation={audioFormValidation}
      audioTypes={audioTypes}
      song={song}
    />
  );
});
